import {
  FlowchartValidationObjectType,
  ProcessFlowchartErrorType,
  ProcessFlowchartForValidation,
  ProcessFlowchartObjectType,
  ProcessFlowchartValidationError,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  FlowchartChangeGenerator,
  FlowchartChanges,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';

/**
 * A flowchart validator is used to run app-specific flowchart validation and self-healing behavior.
 *
 * These validations get run within client/processes/graph-builder-processes/validation/validateProcessFlowchart.ts
 */
export abstract class FlowchartValidator<
  ApplicationValidationError extends object = ProcessFlowchartValidationError,
  ApplicationErrorType extends string = ProcessFlowchartErrorType,
  ApplicationObjectType extends string = ProcessFlowchartObjectType,
> {
  abstract validateFlowchartForType(
    flowchart: ProcessFlowchartForValidation,
    designSpace: ProcessFlowchartDesignSpace,
    didInitialFlowchartHaveChainedRouters: boolean,
    disconnectedNodeIds?: Array<string>,
    applicationValidationContext?: Record<string, unknown>
  ): Array<ProcessFlowchartValidationError>;
  /**
   * A mapping of validation error types to the message to override the default with, if any.
   * Can only override the message for ProcessFlowchartErrorType errors since they are platform-generated errors.
   */
  abstract readonly errorTypeToMessageOverride: Partial<Record<ProcessFlowchartErrorType, string>>;

  /**
   * Heals a flowchart by generating changes in sequence to fix inconsistencies (e.g., data flow edges that map from the wrong section or field types).
   *
   * @returns a function that generates additional self-healing changes based on the current flowchart state
   */
  abstract healFlowchart(changes: FlowchartChanges): FlowchartChangeGenerator;

  /**
   * Gets whether an application-specific error type blocks persistence.
   *
   * Subclasses should override this method to provide application-specific logic for determining
   * whether an error type blocks persistence. If not implementing any application-specific errors, then no need
   * to override this method.
   *
   * @param errorType The application-specific error type to check.
   * @returns Whether the application-specific error type blocks persistence.
   */
  protected getBlocksPersistenceForApplicationErrorType(errorType: ApplicationErrorType): boolean {
    throw new Error('getBlocksPersistenceForApplicationErrorType must be implemented by subclass');
  }

  /**
   * Converts an application-specific object type to a ProcessFlowchartObjectType.
   *
   * Subclasses should override this method to provide application-specific object type conversion logic.
   *
   * @param applicationObjectType The application-specific object type to convert.
   * @returns The corresponding ProcessFlowchartObjectType or the original type if no conversion is needed.
   */
  protected applicationObjectTypeToMaybeOverriddenProcessObjectType(
    applicationObjectType: ApplicationObjectType
  ): FlowchartValidationObjectType {
    throw new Error(
      'applicationObjectTypeToMaybeOverriddenProcessObjectType must be implemented by subclass'
    );
  }

  /**
   * Converts a single application-specific validation error into a ProcessFlowchartValidationError.
   *
   * The objectIdOverrides map is used to override the objectId and parentObjectId with replacement ids.
   * This is needed to support cases where validation errors exist within domain-specific nodes, but
   * we would like to surface them within the task nodes.
   *
   * Subclasses should override this method if they need to convert application-specific validation errors.
   *
   * @param applicationValidationError The application-specific validation error to convert.
   * @param objectIdOverrides A map of object ids to override with their replacement ids.
   * @returns The ProcessFlowchartValidationError with the primary objectId and parent objectId overridden.
   */
  convertApplicationValidationErrorIntoProcessValidationError(
    applicationValidationError: ApplicationValidationError,
    objectIdOverrides: Record<string, string>
  ): ProcessFlowchartValidationError {
    throw new Error(
      'convertApplicationValidationErrorIntoProcessValidationError must be implemented by subclass'
    );
  }

  /**
   * Converts an array of application-specific validation errors into an array of ProcessFlowchartValidationErrors.
   *
   * The objectIdOverrides map is used to override the objectId and parentObjectId with replacement ids.
   * This is needed to support cases where validation errors exist within domain-specific nodes, but
   * we would like to surface them within the task nodes.
   *
   * @param applicationValidationErrors The array of application-specific validation errors to convert.
   * @param objectIdOverrides A map of object ids to override with their replacement ids.
   * @returns The array of ProcessFlowchartValidationErrors with the primary objectId and parent objectId overridden.
   */
  convertApplicationValidationErrorsIntoProcessValidationErrors(
    applicationValidationErrors: Array<ApplicationValidationError>,
    objectIdOverrides: Record<string, string>
  ): Array<ProcessFlowchartValidationError> {
    return applicationValidationErrors.map((applicationValidationError) =>
      this.convertApplicationValidationErrorIntoProcessValidationError(
        applicationValidationError,
        objectIdOverrides
      )
    );
  }
}
